import {Component, OnInit, ViewChild} from '@angular/core';
import { News } from '../../entities/news.model';
import { ApiService } from '../../shared/services/api.service';
import { JhiLanguageService } from 'ng-jhipster';
import { Branch, Marketday, Marketplace } from '../../entities';
import { Principal } from '../../shared';
import slugify from 'slugify';
import {NgbModal, NgbModalOptions, NgbModalRef} from '@ng-bootstrap/ng-bootstrap';
import { CUSTOM_SLUG } from '../../app.constants';

@Component({
  selector: 'app-news-list',
  templateUrl: './news-list.component.html',
  styles: []
})
export class NewsListComponent implements OnInit {

  @ViewChild('newArticleModal') newArticleModal;
  public newsGroups: Array<'overview' | 'global' | 'branch' | 'commune' | 'market'>;
  public selectedNewsGroup: 'overview' | 'global' | 'branch' | 'commune' | 'market' = 'overview';

  public news: News[];
  public allNews: News[];
  public loading = false;
  public loadingNews = false;
  public selectedNews: News;
  public new = false;

  public branches: Branch[];
  public selectedBranch: Branch;
  public selectedBranchId: string = null;

  public communes: Array<string>;
  public selectedCommune: string = null;

  public marketplaces: Marketplace[];
  public marketdays: Marketday[];
  public selectedMarketplace: Marketplace;
  public selectedMarketplaceId: string = null;

  public branchFilter: number = null;
  public communeFilter: string = null;
  public marketplaceFilter: string = null;
  public statusFilter = 'true';
  public error: any;

  private identity;
  private modalRef: NgbModalRef;

  getRowClass = (row: News) => {
    return { 'row--selected': this.selectedNews && row.id === this.selectedNews.id };
  }

  constructor(
    private apiService: ApiService,
    private langService: JhiLanguageService,
    private principal: Principal,
    private modalService: NgbModal,
  ) {
    this.langService.setLocations(['news']);
    this.principal.identity().then((identity) => {
      this.identity = identity;
    });
    this.newsGroups = ['overview', 'global', 'branch', 'commune', 'market'];
  }

  async ngOnInit() {
    await this.getMarketplaces().catch(e => {
      console.log(e);
      this.error = e;
    });
    await this.loadOverview().catch(e => {
      console.log(e);
      this.error = e;
    });
  }

  openModal(modal: any, options: NgbModalOptions) {
    this.modalRef = this.modalService.open(modal, options);
  }

  isManagement(): boolean {
    return (this.identity) ? this.identity.contact.branches === '01 - Geschäftsführung' : false;
  }

  async onNewsGroupChange(): Promise<void> {
    this.news = null;
    switch (this.selectedNewsGroup) {
      case 'global':
        this.news = await this.loadNews('global');
        this.allNews = null;
        break;
      default:
      case 'branch':
      case 'commune':
      case 'market':
        this.allNews = null;
        break;
      case 'overview':
        await this.loadOverview();
        break;
    }
  }

  async getMarketplaces(): Promise<void> {
    const data = await this.loadMarketplaces();
    if (this.identity.contact.branches === '01 - Geschäftsführung') {
      this.marketplaces = data;
      this.communes = data
        .map(mp => mp.city)
        .filter((v, i, a) => a.findIndex(t => (t === v)) === i);
      this.branches = data
        .map(mp => mp.branch)
        .filter((v, i, a) => a.findIndex(t => (t.id === v.id)) === i)
        .sort((a, b) => a.branchNumber > b.branchNumber ? 1 : (a.branchNumber < b.branchNumber ? -1 : 0));
    } else {
      const userBranches = this.identity.contact.branches.split('|');
      this.marketplaces = data
        .filter((market) => userBranches.some((branch) => branch === market.branch.name));
      this.communes = data
        .filter((market) => userBranches.some((branch) => branch === market.branch.name))
        .map(mp => mp.city)
        .filter((v, i, a) => a.findIndex(t => (t === v)) === i);
      this.branches = data
        .filter((market) => userBranches.some((branch) => branch === market.branch.name))
        .map(mp => mp.branch)
        .filter((v, i, a) => a.findIndex(t => (t.id === v.id)) === i)
        .sort((a, b) => a.branchNumber > b.branchNumber ? 1 : (a.branchNumber < b.branchNumber ? -1 : 0));
    }
  }

  /**
   * Load news articles
   */
  loadNews(slug: string, id?: number | string): Promise<News[]> {
    this.loadingNews = true;
    const url = ['backoffice', slug, id, 'news'].filter(s => s !== null).join('/');
    return new Promise((resolve, reject) => {
      this.apiService.find<News>(News, url)
        .subscribe({
          next: (data) => {
            // Sort desc by publishFromDate
            data = data.map(d => new News().deserialize(d));
            data = data.map(item => item.deserialize({...item, marketplaceOriginalId: id}));
            data.sort((a, b) => a.publishFromDate > b.publishFromDate ? -1 : (a.publishFromDate < b.publishFromDate ? 1 : 0));
            this.loadingNews = false;
            resolve(data);
          },
          error: (err) => {
            this.news = [];
            this.loadingNews = false;
            this.error = err;
            console.log(err);
            resolve(false);
          }
        });
    });
  }

  /**
   * Load all marketplaces the current user is responsible for
   */
  loadMarketplaces(): Promise<Marketplace[]> {
    return new Promise((resolve, reject) => {
      this.loading = true;
      this.apiService.find<Marketday>(Marketday, 'marketdays/', {params: {size: 500}})
        .subscribe({
          next: (data) => {
            data.sort((a, b) => a.keyword > b.keyword ? 1 : (a.keyword < b.keyword ? -1 : 0));
            this.loading = false;
            const marketplaces: Marketplace[] = [];
            data.filter((md) => md.active && !md.notInWeb).forEach((md) => {
              if (!marketplaces.some(m => m.id === md.marketplace.id)) {
                marketplaces.push(md.marketplace);
              }
            });
            this.marketdays = [...data];
            resolve(marketplaces);
          },
          error: (err) => {
            this.loading = false;
            reject(err);
          }
        });
    });
  }

  /**
   * Loads all news from all marketplaces
   * @return news array
   */
  async loadOverview() {
    this.loading = true;

    let news = await this.apiService.find(News, 'backoffice/user/news').toPromise()
      .catch(e => {
        this.loading = false;
        throw e;
    });
    news = news.map(n => new News().deserialize(n));

    this.loading = false;
    this.news = news;
    this.allNews = [...news];
    return news;
  }

  getMarketById(id: string | number): Marketplace {
    if (typeof(id) === 'string') {
      id = Number.parseInt(id, 10);
    }
    return this.marketplaces.filter(market => market.id === id)[0];
  }

  getBranchByCommune(commune: string): Branch {
    const result = this.marketdays.find(mp => mp.marketplace.city === commune);
    return result ? result.marketplace.branch : null;
  }

  /**
   * Applies all active filters on this.news
   */
  filterNews(): void {
    this.news = this.allNews;

    if (this.statusFilter) {
      this.news = this.news.filter(item => item.active && this.statusFilter === 'true' || !item.active && this.statusFilter === 'false');
    }

    if (this.branchFilter) {
      this.news = this.news.filter(item => {
        let result = false;
        switch (item.type) {
          case 'global':
            result = true;
            break;
          case 'branch':
            result = this.branchFilter.toString() === item.identifier;
            break;
          case 'commune': {
            const branch = this.getBranchByCommune(item.identifier);
            result = (branch ? branch.id.toString() : null) === this.branchFilter.toString();
            break;
          }
          case 'market': {
            const market = this.getMarketById(item.identifier);
            result = market ? market.branch.id.toString() === this.branchFilter.toString() : false;
            break;
          }
        }
        return result;
      });
    }

    if (this.communeFilter) {
      this.news = this.news.filter(item => {
        let result = false;
        switch (item.type) {
          case 'global':
            result = true;
            break;
          case 'branch': {
            const branch = this.getBranchByCommune(this.communeFilter);
            result = branch ? branch.id.toString() === item.identifier : false;
            break;
          }
          case 'commune':
            result = this.communeFilter === item.identifier;
            break;
          case 'market': {
            const market =  this.getMarketById(item.identifier);
            result = market ? market.city === this.communeFilter : false;
            break;
          }
        }
        return result;
      });
    }

    if (this.marketplaceFilter) {
      this.news = this.news.filter(item => {
        let result = false;
        switch (item.type) {
          case 'global':
            result = true;
            break;
          case 'branch': {
            const market = this.getMarketById(this.marketplaceFilter);
            result = market ? market.branch.id.toString() === item.identifier : false;
            break;
          }
          case 'commune': {
            const market = this.getMarketById(this.marketplaceFilter);
            result = market ? market.city === item.identifier : false;
            break;
          }
          case 'market': {
            const market = this.getMarketById(item.identifier);
            result = market ? market.id.toString(10) === this.marketplaceFilter : false;
            break;
          }
        }
        return result;
      });
    }
  }

  getBranchById(value: string) {
    return this.branches.filter(branch => branch.id === Number.parseInt(value, 10))[0];
  }

  onActivate(event) {}

  async onBranchSelectChange() {
    this.selectedBranch = this.branches.find((branch) => branch.id === Number.parseInt(this.selectedBranchId, 10));
  }

  async onMarketSelectChange() {
    this.selectedMarketplace = this.marketplaces.filter((market) => market.id === Number.parseInt(this.selectedMarketplaceId, 10))[0];
  }

  onSelect(event) {
    if (event.selected.length > 0) {
      this.selectedNews = new News(event.selected[0]);
    }
  }

  openNewsCreateModal() {
    this.selectedBranchId = null;
    this.selectedCommune = null;
    this.selectedMarketplaceId = null;
    this.openModal(this.newArticleModal, {size: 'lg', windowClass: ''});
  }

  getNewsCreateBtnLabel(): string {
    switch (this.selectedNewsGroup) {
      case 'global': return 'Neuigkeit für alle Märkte erstellen';
      case 'branch': return `Neuigkeit für alle Märkte der Niederlassung
      '${this.selectedBranch ? this.selectedBranch.name : ''}' erstellen`;
      case 'commune': return `Neuigkeit für alle Märkte der Kommune '${this.selectedCommune ? this.selectedCommune : ''}' erstellen`;
      case 'market': return `Neuigkeit für Marktplatz '${this.selectedMarketplace ? this.selectedMarketplace.keyword : ''}' erstellen`;
      default: return 'Neuigkeit erstellen';
    }
  }

  onNewsCreate() {
    this.new = true;
    this.selectedNews = null;

    // Update Filters
    this.branchFilter = this.selectedBranchId ? Number.parseInt(this.selectedBranchId, 10) : null;
    this.communeFilter = this.selectedCommune;
    this.marketplaceFilter = this.selectedMarketplaceId;
    this.filterNews();
  }

  onDetailsBack(event) {
    if (event.back) {
      this.selectedNews = null;
      this.new = false;
    }
    if (event.reload) {
      this.news = null;
      setTimeout(async () => {
        slugify.extend(CUSTOM_SLUG);
        switch (this.selectedNewsGroup) {
          case 'overview':
            await this.loadOverview();
            break;
          case 'global':
            this.news = await this.loadNews('global');
            break;
          case 'branch':
            this.news = await this.loadNews('branches', Number.parseInt(this.selectedBranchId, 10));
            break;
          case 'commune':
            this.news = await this.loadNews('communes', slugify(this.selectedCommune, {lower: true}));
            break;
          case 'market':
            this.news = await this.loadNews('marketplaces', Number.parseInt(this.selectedMarketplaceId, 10));
            break;
          default:
            break;
        }
      }, 1000);
    }
    if (event.reset) {
      this.selectedNewsGroup = null;
      this.news = null;
    }
  }

}



// WEBPACK FOOTER //
// ./src/app/news/news-list/news-list.component.ts